import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMingo } from '../context/MingoContext';
import { fetchMyAppUserRow, PROFILE_UPDATED_EVENT } from '../api/profileApi';
import { countUnreadBusinessInquiries } from '../api/businessInquiriesApi';
import { getMyReferralCode, getReferralStats } from '../api/referralApi';
import { shareInviteLink } from '../lib/inviteShare';
import { fetchExpiryReminders } from '../api/expiryReminderApi';
import { getMyPoints } from '../api/pointsApi';
import { getSupabase, isSupabaseConfigured } from '../lib/supabase';
import ActivityScoreBadge from '../components/ActivityScoreBadge';
import UserAvatar from '../components/UserAvatar';
import PlacementAdCard from '../components/PlacementAdCard';
import { usePlacementAdPool } from '../hooks/usePlacementAdPool';
import LoadingState from '../components/LoadingState';
import ErrorState from '../components/ErrorState';
import ContactSetupBanner from '../components/ContactSetupBanner';
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconBell,
  IconBuilding,
  IconCalendar,
  IconCircleCheck,
  IconCoin,
  IconFileText,
  IconReceiptRefund,
  IconGift,
  IconHeart,
  IconInfoCircle,
  IconLanguage,
  IconMessageCircle,
  IconSettings,
  IconShield,
  IconShoppingBag,
  IconSpeakerphone,
  IconStar,
  IconUserOff,
} from '@tabler/icons-react';
import { useChatUnread } from '../hooks/useChatUnread';

function resolveMannerTier(score) {
  const n = Number(score) || 0;
  if (n >= 91) return 'S';
  if (n >= 71) return 'A';
  if (n >= 51) return 'B';
  if (n >= 37) return 'C';
  return 'D';
}

function MyPage({ isDrawerMode = false }) {
  const navigate = useNavigate();
  const { t, isGuest, showToast, isFeatureEnabled } = useMingo();
  const profileAdPool = usePlacementAdPool('profile_feed_cpm', 4);
  const [me, setMe] = useState(null);
  const [stats, setStats] = useState({
    points: 0,
    tradeCount: 0,
    reviewCount: 0,
    noShowCount: 0,
    followerCount: 0,
    followingCount: 0,
    inviteCount: 0,
  });
  const [referralCode, setReferralCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [retryTick, setRetryTick] = useState(0);
  const [unreadBusinessInquiries, setUnreadBusinessInquiries] = useState(0);
  const [urgentExpiryItems, setUrgentExpiryItems] = useState([]);
  const [urgentExpiryCount, setUrgentExpiryCount] = useState(0);
  const [walletPoints, setWalletPoints] = useState(0);
  const pointsSystemOn = isFeatureEnabled('points_system');
  const chatEnabled = isFeatureEnabled('chat');
  const { unreadTotal: chatUnread } = useChatUnread(isGuest);

  const calcDaysUntil = (dateStr) => {
    if (!dateStr) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = new Date(dateStr);
    target.setHours(0, 0, 0, 0);
    return Math.ceil((target - today) / (1000 * 60 * 60 * 24));
  };

  const calcMonthlyDaysUntil = (day) => {
    const n = Number(day);
    if (!Number.isFinite(n) || n < 1 || n > 31) return null;
    const today = new Date();
    const y = today.getFullYear();
    const m = today.getMonth();
    const d = today.getDate();
    const thisMonthLast = new Date(y, m + 1, 0).getDate();
    const thisTarget = Math.min(thisMonthLast, n);
    if (thisTarget >= d) return thisTarget - d;
    const nextMonthLast = new Date(y, m + 2, 0).getDate();
    const nextTarget = Math.min(nextMonthLast, n);
    return thisMonthLast - d + nextTarget;
  };

  const load = useMemo(
    () => async () => {
      setLoading(true);
      setError('');
      const user = await fetchMyAppUserRow();
      if (!user) {
        /* 로그인 직후·DB 미동기 등으로 public.users 행이 없을 때 — 전체서비스 → 내정보에서도 화면 유지 */
        setMe(null);
        setUnreadBusinessInquiries(0);
        setReferralCode('');
        setUrgentExpiryItems([]);
        setUrgentExpiryCount(0);
        setStats((prev) => ({
          ...prev,
          points: 0,
          tradeCount: 0,
          reviewCount: 0,
          noShowCount: 0,
          followerCount: 0,
          followingCount: 0,
          inviteCount: 0,
        }));
        return;
      }
      setMe(user);
      setStats((prev) => ({ ...prev, points: Number(user.points) || 0 }));
      const sb = isSupabaseConfigured ? getSupabase() : null;
      try {
        const [rc, rs] = await Promise.all([getMyReferralCode(user.id), getReferralStats(user.id)]);
        setReferralCode(rc?.code || user.referral_code || '');
        setStats((prev) => ({ ...prev, inviteCount: Number(rs?.invitedCount) || 0 }));
      } catch {
        setReferralCode(user.referral_code || '');
        setStats((prev) => ({ ...prev, inviteCount: 0 }));
      }
      if (sb && user.id) {
        const countOrZero = async (promise) => {
          try {
            const { count, error: err } = await promise;
            if (err) return 0;
            return count ?? 0;
          } catch {
            return 0;
          }
        };
        const [tradeCount, reviewCount, noShowCount, followerCount, followingCount, unreadBiz, expiryList] = await Promise.all([
          countOrZero(sb.from('products').select('*', { count: 'exact', head: true }).eq('author_user_id', user.id)),
          countOrZero(sb.from('guide_reviews').select('*', { count: 'exact', head: true }).eq('user_id', user.id)),
          countOrZero(sb.from('no_show_records').select('*', { count: 'exact', head: true }).eq('user_id', user.id)),
          countOrZero(sb.from('follows').select('*', { count: 'exact', head: true }).eq('following_id', user.id)),
          countOrZero(sb.from('follows').select('*', { count: 'exact', head: true }).eq('follower_id', user.id)),
          countUnreadBusinessInquiries(user.id),
          fetchExpiryReminders(user.id).catch(() => []),
        ]);
        setUnreadBusinessInquiries(unreadBiz);
        const urgent = (expiryList || [])
          .map((item) => {
            const isMonthly = item.reminder_kind === 'monthly';
            const dday = isMonthly ? calcMonthlyDaysUntil(item.monthly_day) : calcDaysUntil(item.expiry_date);
            return { ...item, dday };
          })
          .filter((item) => Number.isFinite(item.dday) && item.dday >= 0 && item.dday <= 7)
          .sort((a, b) => a.dday - b.dday);
        setUrgentExpiryCount(urgent.length);
        setUrgentExpiryItems(urgent.slice(0, 3));
        setStats((prev) => ({
          ...prev,
          tradeCount,
          reviewCount,
          noShowCount,
          followerCount,
          followingCount,
        }));
      } else {
        setUnreadBusinessInquiries(0);
        setUrgentExpiryItems([]);
        setUrgentExpiryCount(0);
      }
    },
    [],
  );

  useEffect(() => {
    if (!pointsSystemOn || !me?.id) return undefined;
    let cancelled = false;
    (async () => {
      const w = await getMyPoints(me.id);
      if (!cancelled) setWalletPoints(Number(w.points) || 0);
    })();
    const onUpdated = () => {
      void getMyPoints(me.id).then((w) => setWalletPoints(Number(w.points) || 0));
    };
    window.addEventListener('mingo-points-updated', onUpdated);
    return () => {
      cancelled = true;
      window.removeEventListener('mingo-points-updated', onUpdated);
    };
  }, [me?.id, pointsSystemOn]);

  useEffect(() => {
    const onProfileUpdated = () => {
      void fetchMyAppUserRow().then((user) => {
        if (user) setMe(user);
      });
    };
    window.addEventListener(PROFILE_UPDATED_EVENT, onProfileUpdated);
    return () => window.removeEventListener(PROFILE_UPDATED_EVENT, onProfileUpdated);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await load();
      } catch (e) {
        if (!cancelled) {
          if (e && e.code === 'OFFLINE') setError(t('errors.offline'));
          else setError(t('errors.generic'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [load, retryTick, t]);

  const manner = Number.isFinite(Number(me?.manner_temperature)) ? Number(me.manner_temperature) : 100;
  const activityScore = manner;
  const mannerSignalCount = Number.isFinite(Number(me?.manner_signal_count)) ? Number(me.manner_signal_count) : 0;
  const isNewManner = mannerSignalCount === 0;
  const mannerTier = isNewManner ? t('myPage.mannerNew') : resolveMannerTier(manner);
  const mannerPercent = isNewManner ? t('myPage.mannerNew') : `${Math.max(0, Math.min(100, manner)).toFixed(1)}%`;
  const mannerBadgeLabel = isNewManner
    ? t('myPage.mannerNewDesc')
    : `${t('myPage.mannerTier')} ${resolveMannerTier(manner)} · ${Math.max(0, Math.min(100, manner)).toFixed(1)}%`;
  const nick = me?.nickname || t('common.appName');
  const userHandle = me?.handle ? String(me.handle) : '';
  const noShowActive = stats.noShowCount > 0;
  const urgentCount = urgentExpiryItems?.length ?? 0;

  const statsData = useMemo(
    () => [
      {
        Icon: IconCoin,
        iconBg: '#fef3c7',
        labelKey: 'myPage.statsPoints',
        value: stats.points,
      },
      {
        Icon: IconCircleCheck,
        iconBg: '#dcfce7',
        labelKey: 'myPage.statsTrades',
        value: stats.tradeCount,
      },
      {
        Icon: IconStar,
        iconBg: '#fff7ed',
        labelKey: 'myPage.statsReviews',
        value: stats.reviewCount,
      },
      {
        Icon: IconAlertCircle,
        iconBg: '#fce7f3',
        labelKey: 'myPage.statsNoShow',
        value: stats.noShowCount,
      },
    ],
    [stats.points, stats.tradeCount, stats.reviewCount, stats.noShowCount],
  );

  const quickMenus = useMemo(
    () => [
      {
        Icon: IconShoppingBag,
        iconBg: '#fef3c7',
        labelKey: 'myPage.myTrades',
        onClick: () => navigate('/my-trades'),
      },
      {
        Icon: IconCalendar,
        iconBg: '#ede9fe',
        labelKey: 'myPage.myReservations',
        onClick: () => navigate('/my-reservations'),
      },
      {
        Icon: IconHeart,
        iconBg: '#fce7f3',
        labelKey: 'myPage.myFavorites',
        onClick: () => navigate('/my-favorites'),
      },
      {
        Icon: IconStar,
        iconBg: '#fff7ed',
        labelKey: 'myPage.myReviews',
        onClick: () => navigate('/community'),
      },
      {
        Icon: IconGift,
        iconBg: '#dcfce7',
        labelKey: 'myPage.invite',
        onClick: () => void shareInviteLink({ t, showToast, referralCode }),
      },
    ],
    [navigate, showToast, t, referralCode],
  );

  const serviceMenus = useMemo(
    () => [
      ...(chatEnabled
        ? [
            {
              id: 'chats',
              Icon: IconMessageCircle,
              iconBg: '#e0e7ff',
              label: t('tabs.chat'),
              badge: chatUnread > 0 ? (chatUnread > 99 ? '99+' : String(chatUnread)) : null,
              onClick: () => navigate('/chats'),
            },
          ]
        : []),
      {
        // 소비자 모드에서 "업체 쪽에 확인할 게 있다"는 최소 신호. 전역 알림벨(useNotificationBadge, 소비자
        // 알림)과는 완전히 분리된 값이며 절대 합산하지 않는다 — 업체 대시보드 진입 시 보이는 카운트
        // (useBusinessActionBadge)의 부분집합(문의/예약)이라 정확히 같지 않을 수 있고, 그건 의도된 동작.
        id: 'biz-dash',
        Icon: IconBuilding,
        iconBg: '#fff7ed',
        label: t('myPage.menu.businessDashboard'),
        badge: unreadBusinessInquiries > 0 ? (unreadBusinessInquiries > 99 ? '99+' : String(unreadBusinessInquiries)) : null,
        onClick: () => navigate('/my-businesses'),
      },
      {
        id: 'events',
        Icon: IconSpeakerphone,
        iconBg: '#fef3c7',
        label: t('myPage.menu.mingoEvents'),
        badge: null,
        onClick: () => navigate('/events'),
      },
      {
        id: 'about',
        Icon: IconInfoCircle,
        iconBg: '#ecfeff',
        label: t('about.title'),
        badge: null,
        onClick: () => navigate('/about'),
      },
      {
        id: 'privacy',
        Icon: IconShield,
        iconBg: '#f0f9ff',
        label: t('privacy.title'),
        badge: null,
        onClick: () => navigate('/privacy'),
      },
      {
        id: 'terms',
        Icon: IconFileText,
        iconBg: '#f5f3ff',
        label: t('terms.title'),
        badge: null,
        onClick: () => navigate('/terms'),
      },
      {
        id: 'refund',
        Icon: IconReceiptRefund,
        iconBg: '#fef2f2',
        label: t('refund.title'),
        badge: null,
        onClick: () => navigate('/refund'),
      },
      {
        id: 'account-settings',
        Icon: IconSettings,
        iconBg: '#ede9fe',
        label: t('myPage.menu.accountSettings'),
        badge: null,
        onClick: () => navigate('/settings/account'),
      },
    ],
    [navigate, t, unreadBusinessInquiries, chatEnabled, chatUnread],
  );

  const settingsMenus = useMemo(
    () => [
      {
        id: 'notif',
        Icon: IconBell,
        iconBg: '#fef3c7',
        label: t('myPage.menu.notificationSettings'),
        onClick: () => navigate('/settings/notifications'),
      },
      {
        id: 'blocked-users',
        Icon: IconUserOff,
        iconBg: '#fee2e2',
        label: t('myPage.menu.blockedUsers'),
        onClick: () => navigate('/settings/blocked-users'),
      },
      {
        id: 'travel-profile',
        Icon: IconCalendar,
        iconBg: '#ede9fe',
        label: t('myPage.menu.travelProfile'),
        onClick: () => navigate('/my/travel-profile'),
      },
      {
        id: 'expiry',
        Icon: IconAlertCircle,
        iconBg: '#fce7f3',
        label: t('expiry.menuLabel'),
        badge: urgentExpiryCount > 0 ? (urgentExpiryCount > 99 ? '99+' : String(urgentExpiryCount)) : null,
        onClick: () => navigate('/expiry-reminder'),
      },
      {
        id: 'lang',
        Icon: IconLanguage,
        iconBg: '#dcfce7',
        label: t('myPage.menu.language'),
        onClick: () => navigate('/onboarding/language?mode=settings'),
      },
    ],
    [navigate, t, urgentExpiryCount],
  );

  if (loading) {
    return (
      <div style={{ background: '#F8F8FC', minHeight: '100vh', padding: 16 }}>
        <LoadingState />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ background: '#F8F8FC', minHeight: '100vh', padding: 16 }}>
        <ErrorState message={error} onRetry={() => setRetryTick((v) => v + 1)} />
      </div>
    );
  }

  return (
    <div
      className={`mingo-page${isDrawerMode ? ' mingo-mypage--drawer' : ' mingo-page--stack'}`}
      style={
        isDrawerMode
          ? { background: '#F8F8FC' }
          : { background: '#F8F8FC', minHeight: '100vh', paddingBottom: 100 }
      }
    >
      {!isDrawerMode && (
      <div className="mingo-page-header mingo-page-header--layout-b">
        <div style={{ width: 40 }} aria-hidden="true" />
        <div className="mingo-page-header__title-wrap">
          <h1 className="mingo-page-header__title">{t('myPage.title')}</h1>
        </div>
        <div className="mingo-page-header__right-slot">
          <button className="mingo-mypage-expiry-btn" onClick={() => navigate('/expiry-reminder')}>
            <span>⏰</span>
            {urgentCount > 0 && <span className="mingo-mypage-expiry-btn__badge">{urgentCount}</span>}
          </button>
        </div>
      </div>
      )}
      <div className={isDrawerMode ? 'mingo-mypage--drawer-body' : 'mingo-page__scroll'}>
        {isDrawerMode ? (
          <div className="mingo-drawer-profile">
            <div className="mingo-drawer-profile__avatar" onClick={() => navigate('/friends', { state: { from: 'mypage' } })}>
              <UserAvatar
                src={me?.avatar_url}
                name={nick}
                size={48}
                className="mingo-drawer-profile__avatar-img"
              />
            </div>
            <div className="mingo-drawer-profile__info">
              <h2 className="mingo-drawer-profile__nick">{nick}</h2>
              <p className="mingo-drawer-profile__sub">
                {t('myPage.following')} {stats.followingCount}
                {' · '}
                {t('myPage.followers')} {stats.followerCount}
              </p>
              <div className="mingo-drawer-profile__grade">
                <ActivityScoreBadge score={activityScore} size="sm" isNew={isNewManner} title={mannerBadgeLabel} ariaLabel={mannerBadgeLabel} />
                <span>{mannerPercent}</span>
              </div>
            </div>
          </div>
        ) : (
          <section className="mingo-mypage-hero">
            <div className="mingo-mypage-hero__bg" aria-hidden="true" />
            <div
              className="mingo-mypage-hero__avatar-wrap"
              onClick={() => navigate('/friends', { state: { from: 'mypage' } })}
              style={{ cursor: 'pointer' }}
            >
              <UserAvatar
                src={me?.avatar_url}
                name={nick}
                size={80}
                className="mingo-mypage-hero__avatar"
              />
            </div>
            <div className="mingo-mypage-hero__info">
              <h2 className="mingo-mypage-hero__nick">{nick}</h2>
              {userHandle ? <p className="mingo-mypage-hero__handle">@{userHandle}</p> : null}
              {pointsSystemOn ? (
                <div className="mingo-points-badge">
                  <span><IconStar size={14} stroke={2} aria-hidden style={{ verticalAlign: -2, marginRight: 4 }} /> {walletPoints.toLocaleString()} P</span>
                  <button
                    type="button"
                    onClick={() => {
                      window.alert(t('common.comingSoon'));
                    }}
                  >
                    {t('translation.charge')}
                  </button>
                </div>
              ) : null}
            </div>
            {activityScore >= 0 ? (
              <div className="mingo-mypage-hero__score mingo-mypage-hero__score--detail">
                <ActivityScoreBadge score={activityScore} size="sm" isNew={isNewManner} title={mannerBadgeLabel} ariaLabel={mannerBadgeLabel} />
                <div className="mingo-mypage-hero__score-meta">
                  <span className="mingo-mypage-hero__score-tier">
                    {t('myPage.mannerTier')}: {mannerTier}
                  </span>
                  <span className="mingo-mypage-hero__score-percent">{mannerPercent}</span>
                </div>
              </div>
            ) : null}
            <div className="mingo-mypage-hero__follow">
              <button type="button" className="mingo-mypage-hero__follow-item">
                <span className="mingo-mypage-hero__follow-num">{stats.followingCount}</span>
                <span className="mingo-mypage-hero__follow-label">{t('myPage.following')}</span>
              </button>
              <div className="mingo-mypage-hero__follow-divider" aria-hidden="true" />
              <button type="button" className="mingo-mypage-hero__follow-item">
                <span className="mingo-mypage-hero__follow-num">{stats.followerCount}</span>
                <span className="mingo-mypage-hero__follow-label">{t('myPage.followers')}</span>
              </button>
            </div>
            <button type="button" className="mingo-mypage-hero__edit-btn" onClick={() => navigate('/friends', { state: { from: 'mypage' } })}>
              {t('friends.hubTitle')}
            </button>
          </section>
        )}
        <ContactSetupBanner />

        <section style={{ marginTop: 12, padding: '0 16px' }}>
          <div
            style={{
              background: '#fff',
              borderRadius: 16,
              boxShadow: '0 2px 8px rgba(15,23,42,0.06)',
              padding: '10px 10px 8px',
            }}
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                gap: 6,
              }}
            >
              {statsData.map((stat) => (
                <button
                  key={stat.labelKey}
                  type="button"
                  onClick={stat.labelKey === 'myPage.statsPoints' ? () => navigate('/points/charge') : undefined}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    borderRadius: 10,
                    padding: '6px 4px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 2,
                    cursor: stat.labelKey === 'myPage.statsPoints' ? 'pointer' : 'default',
                  }}
                >
                  <span
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: '999px',
                      background: stat.iconBg,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {stat.Icon ? <stat.Icon size={18} stroke={1.8} /> : null}
                  </span>
                  <span
                    style={{
                      fontSize: 18,
                      lineHeight: '22px',
                      fontWeight: 800,
                      color: '#111827',
                    }}
                  >
                    {Number(stat.value).toLocaleString()}
                  </span>
                  <span style={{ fontSize: 11, lineHeight: '14px', color: '#6B7280', textAlign: 'center' }}>{t(stat.labelKey)}</span>
                </button>
              ))}
            </div>
            <div style={{ height: 1, background: '#F1F5F9', margin: '8px 0 6px' }} />
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
                gap: 6,
              }}
            >
              {quickMenus.map((menu) => (
                <button
                  key={menu.labelKey || menu.label}
                  type="button"
                  onClick={menu.onClick}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    borderRadius: 10,
                    padding: '4px 2px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 4,
                  }}
                >
                  <span
                    className="mingo-mypage-quick__icon"
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: '999px',
                      background: menu.iconBg,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {menu.Icon ? <menu.Icon size={18} stroke={1.8} /> : null}
                  </span>
                  <span style={{ fontSize: 11, lineHeight: '14px', color: '#6B7280', textAlign: 'center' }}>
                    {menu.labelKey ? t(menu.labelKey) : menu.label}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </section>

        <section style={{ marginTop: 12, padding: '0 16px' }}>
          <PlacementAdCard
            ad={profileAdPool.length ? profileAdPool[0] : null}
            variant="banner"
            slotKey="profile_feed_cpm"
            seed={0}
          />
        </section>

        <section className="mingo-mypage-menu">
          {settingsMenus.map((item) => (
            <button key={item.id} type="button" className="mingo-mypage-menu__item" onClick={item.onClick}>
              <span
                className="mingo-mypage-menu__item-icon"
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: '999px',
                  background: item.iconBg,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {item.Icon ? <item.Icon size={20} stroke={1.8} /> : null}
              </span>
              <span className="mingo-mypage-menu__item-label">{item.label}</span>
              {item.badge ? (
                <span className="mingo-badge mingo-badge--purple">{item.badge}</span>
              ) : null}
              <span className="mingo-mypage-menu__item-arrow">›</span>
            </button>
          ))}
        </section>

        {urgentExpiryItems.length ? (
          <section className="mingo-mypage-expiry-urgent">
            <button type="button" className="mingo-mypage-expiry-urgent__head" onClick={() => navigate('/expiry-reminder')}>
              <span>{t('myPage.expiryUrgentTitle')}</span>
              <span className="mingo-mypage-expiry-urgent__go">›</span>
            </button>
            {urgentExpiryItems.map((item) => (
              <button
                key={item.id}
                type="button"
                className="mingo-mypage-expiry-urgent__item"
                onClick={() => navigate('/expiry-reminder')}
              >
                <span className="mingo-mypage-expiry-urgent__name">{item.title}</span>
                <span className="mingo-mypage-expiry-urgent__dday">
                  {item.dday === 0 ? t('myPage.expiryUrgentToday') : `D-${item.dday}`}
                </span>
              </button>
            ))}
          </section>
        ) : null}

        <section className="mingo-mypage-menu">
          {serviceMenus.map((item) => (
            <button key={item.id} type="button" className="mingo-mypage-menu__item" onClick={item.onClick}>
              <span
                className="mingo-mypage-menu__item-icon"
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: '999px',
                  background: item.iconBg,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {item.Icon ? <item.Icon size={20} stroke={1.8} /> : null}
              </span>
              <span className="mingo-mypage-menu__item-label">{item.labelKey ? t(item.labelKey) : item.label}</span>
              {item.badge ? (
                <span className="mingo-badge mingo-badge--purple">{item.badge}</span>
              ) : null}
              <span className="mingo-mypage-menu__item-arrow">›</span>
            </button>
          ))}
        </section>

        <section style={{ marginTop: 12, padding: '0 16px' }}>
          <PlacementAdCard
            ad={profileAdPool.length ? profileAdPool[1 % profileAdPool.length] : null}
            variant="mini"
            slotKey="profile_feed_cpm"
            seed={1}
          />
        </section>

        {noShowActive ? (
          <section style={{ marginTop: 12, padding: '0 16px' }}>
            <button
              type="button"
              onClick={() => navigate('/penalty-recovery')}
              style={{
                width: '100%',
                border: 'none',
                borderRadius: 12,
                background: '#F87171',
                color: '#fff',
                padding: '12px 14px',
                textAlign: 'left',
                fontWeight: 800,
              }}
            >
              <IconAlertTriangle size={14} stroke={2} aria-hidden style={{ verticalAlign: -2, marginRight: 4 }} /> {t('myPage.noShowRecovery')}
            </button>
          </section>
        ) : null}

        {!isGuest && referralCode ? (
          <section style={{ marginTop: 12, padding: '0 16px' }}>
            <div style={{ background: '#fff', borderRadius: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <div>
                <p style={{ margin: 0, fontSize: 13, color: '#6B7280' }}>{t('myPage.referralTitle')}</p>
                <p style={{ margin: '6px 0 8px', fontSize: 18, fontWeight: 800 }}>{referralCode}</p>
                <p style={{ margin: 0, fontSize: 12, color: '#6B7280' }}>{t('myPage.referralCount', { n: stats.inviteCount })}</p>
              </div>
              <button
                type="button"
                onClick={() => void shareInviteLink({ t, showToast, referralCode })}
                style={{
                  flexShrink: 0,
                  border: 'none',
                  borderRadius: 12,
                  padding: '10px 16px',
                  fontSize: 13,
                  fontWeight: 700,
                  color: '#fff',
                  background: '#7C6CF0',
                }}
              >
                {t('myPage.referralInviteBtn')}
              </button>
            </div>
          </section>
        ) : null}
      </div>
    </div>
  );
}

export default MyPage;
